import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgentRole} from 'Users/models/User/AgentUserType';
import {FieldSalesLeader} from 'Users/models/User/Agent/FieldSalesLeader';

export class UnvalidatedFieldVicePresidentUserType {
  public readonly key = 'UnvalidatedFieldVicePresidentUserType';
  public readonly agentRole = AgentRole.FieldVicePresident;

  constructor(public readonly fieldSalesLeaders: FieldSalesLeader[]) {}
}

export class InvalidFieldVicePresidentUserType {
  public readonly key = 'InvalidFieldVicePresidentUserType';
  public readonly agentRole = AgentRole.FieldVicePresident;

  constructor(public readonly fieldSalesLeaders: FieldSalesLeader[]) {}
}

export class ValidFieldVicePresidentUserType {
  public static create(fieldSalesLeaders: FieldSalesLeader[]) {
    return pipe(
      NEA.fromArray(fieldSalesLeaders),
      O.fold(
        (): FieldVicePresidentUserType => new InvalidFieldVicePresidentUserType(fieldSalesLeaders),
        nonEmptyFieldVicePresidents => new ValidFieldVicePresidentUserType(nonEmptyFieldVicePresidents)
      )
    );
  }

  public readonly key = 'ValidFieldVicePresidentUserType';
  public readonly agentRole = AgentRole.FieldVicePresident;
  private constructor(public readonly fieldSalesLeaders: NEA.NonEmptyArray<FieldSalesLeader>) {}
}

export const EmptyFieldVicePresidentUserType = new UnvalidatedFieldVicePresidentUserType([]);

export const loadFieldSalesLeaders = (fieldSalesLeaders: FieldSalesLeader[]) =>
  ValidFieldVicePresidentUserType.create(fieldSalesLeaders);

export const addFieldSalesLeader = (fieldSalesLeader: FieldSalesLeader) => ({
  fieldSalesLeaders,
}: FieldVicePresidentUserType) => ValidFieldVicePresidentUserType.create([fieldSalesLeader, ...fieldSalesLeaders]);

export const removeFieldSalesLeader = (fieldSalesLeader: FieldSalesLeader) => ({
  fieldSalesLeaders,
}: FieldVicePresidentUserType) =>
  ValidFieldVicePresidentUserType.create(fieldSalesLeaders.filter(fvp => fvp.id !== fieldSalesLeader.id));

export type FieldVicePresidentUserType =
  | ValidFieldVicePresidentUserType
  | InvalidFieldVicePresidentUserType
  | UnvalidatedFieldVicePresidentUserType;

export type FieldVicePresidentUserTypeOptions<TReturn> = ToMatchOptions<FieldVicePresidentUserType, 'key', TReturn>;

export const matchFieldVicePresidentUserType = <TReturn>(
  fvpUserType: FieldVicePresidentUserType,
  options: FieldVicePresidentUserTypeOptions<TReturn>
): TReturn => {
  switch (fvpUserType.key) {
    case 'InvalidFieldVicePresidentUserType':
      return options.InvalidFieldVicePresidentUserType(fvpUserType);
    case 'UnvalidatedFieldVicePresidentUserType':
      return options.UnvalidatedFieldVicePresidentUserType(fvpUserType);
    case 'ValidFieldVicePresidentUserType':
      return options.ValidFieldVicePresidentUserType(fvpUserType);
  }
};
